import { PageOptions } from '@graphcommerce/framer-next-pages'
import { HygraphPagesQuery } from '@graphcommerce/graphcms-ui'
import { PageMeta, StoreConfigDocument } from '@graphcommerce/magento-store'
import {
  LayoutTitle,
  GetStaticProps,
  LayoutOverlay,
  LayoutOverlayHeader,
  LayoutOverlayProps,
  MetaRobots,
} from '@graphcommerce/next-ui'
import { Trans } from '@lingui/react'
import Box from '@mui/material/Box'
import { StoreLocator } from '../components/StoreLocator/StoreLocator'
import { StoreLocatorMapLoader } from '../components/StoreLocator/StoreLocatorMapLoader'
import { DefaultPageDocument } from '../graphql/DefaultPage.gql'
import { graphqlSharedClient, graphqlSsrClient } from '../lib/graphql/graphqlSsrClient'

type GetPageStaticProps = GetStaticProps<LayoutOverlayProps>

type StoreLocatorOverlayProps = HygraphPagesQuery

function StoreLocatorOverlay(props: StoreLocatorOverlayProps) {
  if (!import.meta.graphCommerce.googleMapsApi && process.env.NODE_ENV !== 'production')
    return <div>No valid Google API key configured</div>

  const { pages } = props
  const page = pages?.[0]

  return (
    <Box
      sx={(theme) => ({
        display: 'grid',
        gridTemplateRows: 'auto 1fr',
        height: '100vh',

        '& .LayoutHeader-root.noAlign': {
          position: 'relative',
          mb: 0,
        },
        '& .LayoutHeaderContent-bg': {
          display: 'none',
        },
        '& .LayoutHeaderContent-content': {
          color: theme.palette.secondary.contrastText,
          backgroundColor: theme.palette.primary.main,
          position: 'relative',
        },
        '& .LayoutHeaderContent-content .MuiButtonBase-root': {
          '&:hover, &:focus, &:active': {
            color: theme.palette.text.primary,
          },

          [theme.breakpoints.down('md')]: {
            width: '30px',
            height: '30px',
            minWidth: '30px',
            minHeight: '30px',

            '& .IconSvg-root': {
              fontSize: theme.typography.h4.fontSize,
            },
          },
        },
      })}
    >
      <PageMeta
        title={page?.metaTitle ?? page?.title ?? ''}
        metaDescription={page?.metaDescription ?? ''}
        metaRobots={page?.metaRobots.toLowerCase().split('_') as MetaRobots[] | undefined}
      />
      <LayoutOverlayHeader switchPoint={0}>
        <LayoutTitle component='span' size='small' variant='h3'>
          <Trans id='Store Locator' />
        </LayoutTitle>
      </LayoutOverlayHeader>
      <StoreLocatorMapLoader>
        <StoreLocator />
      </StoreLocatorMapLoader>
    </Box>
  )
}

const pageOptions: PageOptions<LayoutOverlayProps> = {
  overlayGroup: 'left',
  Layout: LayoutOverlay,
  layoutProps: {
    justifySm: 'stretch',
    sizeSm: 'full',
    variantSm: 'bottom',
    variantMd: 'left',
    justifyMd: 'start',
    widthMd: 'max(50vw, 920px)',
  },
}
StoreLocatorOverlay.pageOptions = pageOptions

export default StoreLocatorOverlay

export const getStaticProps: GetPageStaticProps = async ({ locale }) => {
  const client = graphqlSharedClient(locale)
  const staticClient = graphqlSsrClient(locale)
  const conf = client.query({ query: StoreConfigDocument })
  const rootCategory = (await conf).data.storeConfig?.root_category_uid ?? ''

  const page = staticClient.query({
    query: DefaultPageDocument,
    variables: { url: 'winkelzoeker', rootCategory },
  })

  return {
    props: {
      ...(await page).data,
      apolloState: await conf.then(() => client.cache.extract()),
    },
    revalidate: 60 * 20,
  }
}
